document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-configuration]').forEach((configuration) => {
    const inputs = [...configuration.querySelectorAll('[data-configuration-option]')];
    const summary = configuration.querySelector('[data-configuration-summary]');
    const total = configuration.querySelector('[data-configuration-total]');
    const form = configuration.closest('form');
    const mainQuantityInput = form?.querySelector('[name="quantity"]');
    const currency = configuration.dataset.currency || 'CHF';
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
      style: 'currency',
      currency
    });

    const inputByVariantId = new Map(inputs.map((input) => [String(input.value), input]));
    const shellFor = (input) => input.closest('[data-configuration-option-shell]');
    const quantityInputFor = (input) => shellFor(input)?.querySelector('[data-configuration-quantity]');
    const quantityMode = (input) => input.dataset.configurationQuantityMode || 'inherit';
    const mainQuantity = () => Math.max(
      1,
      Number.parseInt(mainQuantityInput?.value || '1', 10) || 1
    );
    const selectedQuantity = (input) => {
      if (!input.checked) return 0;
      if (quantityMode(input) === 'singleton') return 1;
      if (quantityMode(input) === 'multiple') {
        return Math.max(0, Number.parseInt(quantityInputFor(input)?.value || '0', 10) || 0);
      }
      const multiplier = Math.min(99, Math.max(
        1, Number.parseInt(input.dataset.configurationQuantityMultiplier || '1', 10) || 1
      ));
      return mainQuantity() * multiplier;
    };
    const pairedInput = (input) => inputByVariantId.get(String(input.dataset.configurationPairId || ''));

    const setQuantity = (input, requestedQuantity, { manual = false } = {}) => {
      const field = quantityInputFor(input);
      if (!field) return;
      const quantity = Math.min(999, Math.max(0, Number.parseInt(requestedQuantity, 10) || 0));
      field.value = String(quantity);
      input.checked = quantity > 0;
      if (manual && input.dataset.configurationPairRole === 'companion') {
        input.dataset.quantitySynced = 'false';
      }

      if (input.dataset.configurationPairRole === 'primary') {
        const companion = pairedInput(input);
        if (companion?.dataset.quantitySynced === 'true') {
          setQuantity(companion, quantity);
          if (quantity === 0) companion.dataset.quantitySynced = 'false';
        }
      }
    };

    const synchronizeDependencies = () => {
      inputs.forEach((input) => {
        const dependency = input.dataset.configurationDependency;
        const nativeDisabled = input.dataset.nativeDisabled === 'true';
        const dependencySelected = !dependency || inputs.some(
          (candidate) => candidate.checked && candidate.dataset.configurationGroup === dependency
        );

        input.disabled = nativeDisabled || !dependencySelected;
        if (input.disabled) {
          input.checked = false;
          if (quantityMode(input) === 'multiple') setQuantity(input, 0);
        }
        shellFor(input)?.toggleAttribute(
          'data-dependency-pending',
          Boolean(dependency && !dependencySelected)
        );
      });
    };

    const update = () => {
      const selected = inputs.filter((input) => input.checked && selectedQuantity(input) > 0);
      const cents = selected.reduce(
        (result, input) => (
          result
          + Number.parseInt(input.dataset.priceCents || '0', 10) * selectedQuantity(input)
        ),
        0
      );
      summary.hidden = selected.length === 0;
      total.textContent = `+ ${formatter.format(cents / 100)}`;
      configuration.dispatchEvent(new CustomEvent('configuration:change', {
        bubbles: true,
        detail: {
          selected: selected.map((input) => ({
            variantId: input.value,
            quantity: selectedQuantity(input)
          })),
          cents
        }
      }));
    };

    const enforceExclusiveGroup = (input) => {
      if (!input.checked || !input.dataset.configurationGroup) return;
      inputs.forEach((candidate) => {
        if (
          candidate !== input
          && candidate.dataset.configurationGroup === input.dataset.configurationGroup
        ) {
          candidate.checked = false;
          if (quantityMode(candidate) === 'multiple') setQuantity(candidate, 0);
        }
      });
    };

    const enforceRequiredGroups = () => {
      const requiredGroups = new Set(inputs
        .filter((input) => input.dataset.configurationSelectionMode === 'single_required')
        .map((input) => input.dataset.configurationGroup)
        .filter(Boolean));
      requiredGroups.forEach((group) => {
        const groupInputs = inputs.filter(
          (input) => input.dataset.configurationGroup === group && !input.disabled
        );
        if (!groupInputs.some((input) => input.checked) && groupInputs[0]) {
          groupInputs[0].checked = true;
          if (quantityMode(groupInputs[0]) === 'multiple') setQuantity(groupInputs[0], 1);
        }
      });
    };

    inputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (quantityMode(input) === 'multiple') {
          if (!input.checked) {
            setQuantity(input, 0);
          } else if (selectedQuantity(input) === 0) {
            const primary = input.dataset.configurationPairRole === 'companion'
              ? pairedInput(input)
              : null;
            const initialQuantity = primary ? selectedQuantity(primary) : 1;
            setQuantity(input, Math.max(1, initialQuantity));
            if (primary && selectedQuantity(primary) > 0) input.dataset.quantitySynced = 'true';
          }
        }
        enforceExclusiveGroup(input);
        synchronizeDependencies();
        enforceRequiredGroups();
        update();
      });

      const shell = shellFor(input);
      const quantityField = quantityInputFor(input);
      shell?.querySelector('[data-configuration-quantity-decrease]')?.addEventListener('click', () => {
        setQuantity(input, selectedQuantity(input) - 1, { manual: true });
        synchronizeDependencies();
        enforceRequiredGroups();
        update();
      });
      shell?.querySelector('[data-configuration-quantity-increase]')?.addEventListener('click', () => {
        setQuantity(input, selectedQuantity(input) + 1, { manual: true });
        enforceExclusiveGroup(input);
        synchronizeDependencies();
        enforceRequiredGroups();
        update();
      });
      quantityField?.addEventListener('change', () => {
        setQuantity(input, quantityField.value, { manual: true });
        enforceExclusiveGroup(input);
        synchronizeDependencies();
        enforceRequiredGroups();
        update();
      });
    });

    mainQuantityInput?.addEventListener('input', update);
    mainQuantityInput?.addEventListener('change', update);
    synchronizeDependencies();
    enforceRequiredGroups();
    update();
  });
});
